import React, { useState, useEffect, useRef } from 'react';
import { 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Drawer, 
  Box, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  Paper,
  Divider,
  ListItemIcon,
  ListItemText,
  // Tooltip,
  Button,
  useTheme,
  //SwipeableDrawer,
  Collapse,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Menu as MenuIcon, 
  //MoreVert as MoreVertIcon, 
  Apps as AppsIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Lightbulb as LightbulbIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  // Storage as StorageIcon,
  CloudQueue as CloudIcon,
  //InsertDriveFile as DocIcon,
  //TableChart as SheetIcon,
  //Slideshow as SlideIcon,
  // ArrowDropDown as ArrowDropDownIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Edit as EditIcon,
  //ExpandMore as ExpandMoreIcon,
  //ChevronRight as ChevronRightIcon,
  //Psychology,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  //Home as HomeIcon,
  LibraryBooks as LibraryBooksIcon,
  Create as CreateIcon,
  Home as HomeIcon,
  //FileText as FileTextIcon,
  BarChart as BarChartIcon,
  Rocket as RocketIcon,
  Settings as ToolsIcon,
  //MessageSquare as MessageSquareIconn
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  //FileText,
  //Table,
  Users as ExecutivesIcon,
  BarChart2 as AnalyzeIcon,
  ClipboardList as PlanIcon,
  FileCheck as SolutionsIcon,
  DollarSign as DollarSignIcon,
  DoorClosed as CloseIcon,
  Code as TechIcon,
} from 'lucide-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { v4 as uuidv4 } from 'uuid';
import FloatingNotifications, { AppNotification } from './FloatingNotifications.tsx';

// Import custom components
import StrategicPlanStepper from '../../components/executives/StrategicPlanStepper.tsx';
import MarketingPlanStepper from '../../components/executives/MarketingPlanStepper.tsx';
import TechRoadmapStepper from '../../components/executives/TechnologyRoadmapStepper.tsx';
import FinancialPlanStepper from '../../components/executives/FinancialPlanStepper.tsx';
import ProductStrategyStepper from '../../components/executives/ProductStrategyStepper.tsx';
import WelcomeGuide from '../../components/workspace/WelcomeGuide.tsx';
import GradientText from '../../components/common/GradientText.tsx';
import InfoTooltip from '../../components/common/InfoTooltip.tsx';
import BackgroundAnimation from '../Landing/components/BackgroundAnimation.tsx';
import ProposalCard from '../Landing/components/ProposalCard.tsx';
import { proposals, solutions } from '../../data/mockData.ts';
import { Executive } from '../../types/executive.ts';
import SurrogateRoleInfo from '../../components/executives/SurrogateRoleInfo.tsx';
import { surrogateRoles, SurrogateRole } from '../../data/surrogateRoles.ts';
import ProposalDetailContent from '../../components/workspace/ProposalDetailContent.tsx';
import SolutionDetailContent from '../../components/workspace/SolutionDetailContent.tsx';
import { solutions as extraSolutions } from '../../data/solutionsData.ts';
import MainFunctionCards from '../../components/problemWorkspace/MainFunctionCards.tsx';
import ProblemSolvingToolboxContent from '../../components/problemWorkspace/ProblemSolvingToolboxContent.tsx';
import DocumentTools from '../../components/workspace/DocumentTools.tsx';
import DocumentEditor from '../../components/workspace/DocumentEditor.tsx';
import SpreadsheetEditor from '../../components/workspace/SpreadsheetEditor.tsx';
import SavvyDataProposalContent from './SavvyDataProposalContent.tsx';
import SavvyDataProposalCard from '../../components/savvydata/SavvyDataProposalCard.tsx';
import BusinessPlanStepper from '../../components/executives/BusinessPlanStepper.tsx';
import NavigationStepper from '../../components/navigationStepper/navigationStepper.tsx';
import BlankProposalTemplate from '../../components/proposal/BlankProposalTemplate.tsx';
import SavvyDataDemoContent from './SavvyDataDemoContent.tsx';
import ExecutiveStepper from '../../components/executives/ExecutiveStepper.tsx';
import PlanStepper from '../../components/proposal/PlanStepper.tsx';
import EditableProposalCard from '../../components/workspace/EditableProposalCard.tsx';

// Define the executive data
const executivesData: Executive[] = [
  {
    id: 'ceo-1',
    name: 'Alex Morgan',
    role: 'CEO',
    title: 'Chief Executive Officer',
    specialty: 'Strategic Leadership',
    status: 'available',
    rating: 4.9,
    completedTasks: 156,
    section: 'capital',
    insight: 'Our customer service response times have increased by 45% over the last quarter, leading to decreased satisfaction scores. We need an AI-powered solution that can handle routine inquiries while maintaining a personal touch.',
    icon: <PeopleIcon sx={{ color: '#8A2BE2' }} />,
  },
  {
    id: 'cto-1',
    name: 'Taylor Reed',
    role: 'CTO',
    title: 'Chief Technology Officer',
    specialty: 'Technical Architecture',
    status: 'available',
    rating: 4.7,
    completedTasks: 118,
    section: 'operations',
    insight: 'We need to improve our technology roadmap to better support our business needs.',
    icon: <TechIcon sx={{ color: '#00CC99' }} />,
  },
  {
    id: 'cpo-1',
    name: 'Riley Johnson',
    role: 'CPO',
    title: 'Chief Product Officer',
    specialty: 'Product Development',
    status: 'available',
    rating: 4.7,
    completedTasks: 87,
    section: 'operations',
    insight: 'Our product strategy needs to be more focused on customer needs and market trends.',
    icon: <RocketIcon sx={{ color: '#8A2BE2' }} />,
  },
  {
    id: 'cmo-1',
    name: 'Jordan Smith',
    role: 'CMO',
    title: 'Chief Marketing Officer',
    specialty: 'Marketing Strategy',
    status: 'available',
    rating: 4.6,
    completedTasks: 94,
    section: 'operations',
    insight: 'We need to develop a more effective marketing plan to increase customer satisfaction.',
    icon: <BarChartIcon sx={{ color: '#00CC99' }} />,
  },
  {
    id: 'cfo-1',
    name: 'Jamie Chen',
    role: 'CFO',
    title: 'Chief Financial Officer',
    specialty: 'Financial Strategy',
    status: 'available',
    rating: 4.8,
    completedTasks: 132,
    section: 'capital',
    insight: 'Our financial strategy needs to be more aligned with our business goals.',
    icon: <DollarSignIcon sx={{ color: '#8A2BE2' }} />,
  }
];

//const RoundImage = styled('img')(({ theme }) => ({
//  width: '9%',
//  height: '9%',
//  borderRadius: '50%',
//  objectFit: 'cover',
//}));

// Define the main functionality options
const mainFunctionOptions = [
  { id: 'proposal', label: 'Create Proposal', icon: <CreateIcon />},
  { id: 'improve', label: 'Improve Statement', icon: <DescriptionIcon /> },
  { id: 'similar', label: 'Similar Problems', icon: <LightbulbIcon /> },
  { id: 'solutions', label: 'View Solutions', icon: <DashboardIcon /> },
  { id: 'surrogates', label: 'Surrogates', icon: <PeopleIcon /> },
  { id: 'toolbox', label: 'Knowledge Toolbox', icon: <BuildIcon /> },
  { id: 'businessPlan', label: 'Business Plan', icon: <BusinessIcon /> },
];

// Define the bento menu options
const bentoOptions = [
  { id: 'create', label: 'Create', icon: <PlanIcon /> },
  { id: 'executives', label: 'Executives', icon: <ExecutivesIcon /> },
  { id: 'solutions', label: 'Solutions', icon: <SolutionsIcon /> },
  { id: 'analyze', label: 'Analyze', icon: <AnalyzeIcon /> },
  { id: 'chrome', label: 'Browse', icon: <CloudIcon /> },
  { id: 'api', label: 'API', icon: <CodeIcon /> },
  { id: 'library', label: 'Solution Library', icon: <LibraryBooksIcon /> },
];

// Styled components
const WorkspaceContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(26, 26, 46, 0.8))',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
}));

const BackgroundOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at center, rgba(138, 43, 226, 0.1), transparent 70%)',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  justifyContent: 'space-between',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
}));

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  backgroundColor: selected ? 'rgba(138, 43, 226, 0.2)' : 'transparent',
  '&:hover': {
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.3)',
  }
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: 'rgba(26, 26, 46, 0.9)',
    backdropFilter: 'blur(3px)',
    borderRight: '1px solid rgba(138, 43, 226, 0.2)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    width: 320,
  },
}));

const ExecutiveCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'executive'
})<{ executive?: Executive }>(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(3px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const SplitScreenContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 'calc(100vh - 64px)',
  position: 'relative',
  zIndex: 1,
  '@media (max-width: 600px)': {
    flexDirection: 'column',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(to bottom, #00CC99, #8A2BE2)',
    backgroundSize: '100% 100%',
    animation: 'gradient 6s linear infinite',
  },
}));

const LeftPane = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  flex: expanded ? '0' : '0.40', // Change from 1 to 0.35 (35%)
  width: expanded ? '0' : '40%', // Add explicit width
  borderRight: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  overflowY: 'auto',
  background: 'rgba(26, 26, 46, 0.5)',
  backdropFilter: 'blur(1px)',
  display: expanded ? 'none' : 'block',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    display: expanded ? 'none' : 'block',
    width: expanded ? '0' : '100%',
    flex: 'none',
    height: expanded ? '0' : 'auto',
  },
}));

const RightPane = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'ref'
})<{ expanded?: boolean }>(({ theme, expanded }) => ({
  flex: expanded ? 1 : '0.65', // Change from 1 to 0.65 (65%)
  width: expanded ? '100%' : '65%', // Change from 50% to 65%
  padding: theme.spacing(3),
  overflowY: 'auto',
  background: 'rgba(26, 26, 46, 0.3)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    flex: 'none',
    height: expanded ? 'calc(100vh - 64px)' : 'auto',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(26, 26, 46, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(138, 43, 226, 0.2)',
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    color: '#fff',
  },
}));

const DrawerHandle = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5, 2),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderTopLeftRadius: theme.spacing(1.5),
  borderTopRightRadius: theme.spacing(1.5),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  borderBottom: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.2)',
  zIndex: 10,
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.9)',
    boxShadow: '0 -4px 25px rgba(138, 43, 226, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
  },
}));

const BentoGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const BentoItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const FullscreenButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  color: '#00FFFF',
  zIndex: 10,
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
  },
}));


const ProblemWorkspace: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  
  // State for the problem statement
  const [problemStatement, setProblemStatement] = useState<string>(
    'Our customer service response times have increased by 45% over the last quarter, leading to decreased satisfaction scores. We need an AI-powered solution that can handle routine inquiries while maintaining a personal touch.'
  );
  
  // State for problem Text
  const [problemText, setProblemText] = useState<string>('');
  
  // State for the selected executive
  const [selectedExecutive, setSelectedExecutive] = useState<Executive | null>(
    executivesData.find(exec => exec.role === 'CMO') || null
  );
  
  // State for the selected functionality
  const [selectedFunction, setSelectedFunction] = useState<string>('surrogates');
  
  // State for drawers and menus
  const [mainFunctionAnchorEl, setMainFunctionAnchorEl] = useState<null | HTMLElement>(null);
  const [executivesDrawerOpen, setExecutivesDrawerOpen] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [refineAnchorEl, setRefineAnchorEl] = useState<null | HTMLElement>(null);
  // eslint-disable-next-line no-unused-vars
  const [enhanceAnchorEl, setEnhanceAnchorEl] = useState<null | HTMLElement>(null);
  const [bentoAnchorEl, setBentoAnchorEl] = useState<null | HTMLElement>(null);
  
  // State for recommendations drawer
  const [recommendationsOpen, setRecommendationsOpen] = useState<boolean>(false);
  
  // State for active recommendation sections
  const [activeRecommendations, setActiveRecommendations] = useState<{
    executive: boolean;
    proposals: boolean;
    function: boolean;
    refine: boolean;
    enhance: boolean;
  }>({
    executive: true,
    proposals: true,
    function: false,
    refine: false,
    enhance: false
  });

  const [selectedTool, setSelectedTool] = useState<'solutions' | 'analyze' | 'plan' | 'executives' | 'docs' | null>(null);
  
  // Selected options for recommendations
  const [selectedRefineOption, setSelectedRefineOption] = useState<string | null>(null);
  const [selectedEnhanceOption, setSelectedEnhanceOption] = useState<string | null>(null);
  
  // State for surrogate role information
  const [roleInfoMap, setRoleInfoMap] = useState(surrogateRoles);
  
  // State to track if welcome guide has been dismissed
  const [showWelcomeGuide, setShowWelcomeGuide] = useState<boolean>(true);
  
  // Add state for background animation
  const [showBackgroundAnimation, setShowBackgroundAnimation] = useState<boolean>(true);
  
  // Add state variables for tracking selected IDs
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  
  // Add near the top of the component with the other useEffect hooks
  const rightPaneRef = useRef<HTMLDivElement>(null);

  // Add this useEffect to scroll the right pane to top when content changes
  useEffect(() => {
    if (rightPaneRef.current) {
      rightPaneRef.current.scrollTop = 0;
    }
  }, [selectedFunction, selectedExecutive, selectedProposalId, selectedSolutionId]);
  
  // Use the problem statement from the location state if available
  useEffect(() => {
    if (location.state && location.state.problemStatement) {
      setProblemStatement(location.state.problemStatement);
    }
  }, [location.state]);
  
  // Add this helper function near the top of the component with other handler functions
  const dismissWelcomeGuideAndDo = (action: () => void) => {
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
    // Perform the requested action
    action();
  };
  
  // Toggle recommendations drawer
  const toggleRecommendations = () => {
    dismissWelcomeGuideAndDo(() => setRecommendationsOpen(!recommendationsOpen));
  };
  
  // Handlers for main function menu
  const handleMainFunctionClick = (event: React.MouseEvent<HTMLElement>) => {
    setMainFunctionAnchorEl(event.currentTarget);
  };
  
  const handleMainFunctionClose = () => {
    setMainFunctionAnchorEl(null);
  };
  
  // Update the handleMainFunctionSelect function to handle the navigator option:
  const handleMainFunctionSelect = (functionId: string) => {
    if (functionId === 'similar') {
      // Show proposals instead
      handleFunctionSelection('proposals');
    setActiveRecommendations({
      ...activeRecommendations,
        proposals: true
    });
      
    // Open recommendations drawer if it's closed
    if (!recommendationsOpen) {
      setRecommendationsOpen(true);
    }
    } else {
      handleFunctionSelection(functionId);
    }
    handleMainFunctionClose();
  };
  
  // Toggle executives drawer
  const toggleExecutivesDrawer = () => {
    setExecutivesDrawerOpen(!executivesDrawerOpen);
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
  };
  
  // Handle executive selection
  const handleExecutiveSelect = (executive: Executive) => {
    // Always hide welcome guide when an executive is selected
    setShowWelcomeGuide(false);
    
    setSelectedExecutive(executive);
    
    // Set the function to display surrogates content in the right pane
    setSelectedFunction('surrogates');
    
    // Update active recommendations
    const updatedRecommendations = { ...activeRecommendations };
    Object.keys(updatedRecommendations).forEach(key => {
      updatedRecommendations[key as keyof typeof activeRecommendations] = false;
    });
    setActiveRecommendations(updatedRecommendations);
  };
  
  // Handlers for refine menu
  const handleRefineClick = (event: React.MouseEvent<HTMLElement>) => {
    setRefineAnchorEl(event.currentTarget);
  };
  
  const handleRefineClose = () => {
    setRefineAnchorEl(null);
  };
  
  // eslint-disable-next-line no-unused-vars
  const handleRefineSelect = (option: string) => {
    setSelectedRefineOption(option);
    setActiveRecommendations({
      ...activeRecommendations,
      refine: true
    });
    handleRefineClose();
    // Open recommendations drawer if it's closed
    if (!recommendationsOpen) {
      setRecommendationsOpen(true);
    }
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
  };
  
  // Handlers for enhance menu
  const handleEnhanceClick = (event: React.MouseEvent<HTMLElement>) => {
    setEnhanceAnchorEl(event.currentTarget);
  };
  
  const handleEnhanceClose = () => {
    setEnhanceAnchorEl(null);
  };
  
  // eslint-disable-next-line no-unused-vars
  const handleEnhanceSelect = (option: string) => {
    setSelectedEnhanceOption(option);
    setActiveRecommendations({
      ...activeRecommendations,
      enhance: true
    });
    handleEnhanceClose();
    // Open recommendations drawer if it's closed
    if (!recommendationsOpen) {
      setRecommendationsOpen(true);
    }
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
  };
  
  // Handlers for bento menu
  const handleBentoClick = (event: React.MouseEvent<HTMLElement>) => {
    setBentoAnchorEl(event.currentTarget);
  };
  
  const handleBentoClose = () => {
    setBentoAnchorEl(null);
  };

  const navigate = useNavigate();
  
  // Handle surrogate role info change
  const handleRoleInfoChange = (role: string, updatedInfo: SurrogateRole) => {
    setRoleInfoMap({
      ...roleInfoMap,
      [role]: updatedInfo
    });
  };
  
  // Handle dismissing the welcome guide
  const handleDismissWelcomeGuide = () => {
    // Only hide the welcome guide
    setShowWelcomeGuide(false);
    // Default to 'Plan' view when the welcome guide is dismissed
    setSelectedFunction('Create');
  };
  
  // Remove a recommendation
  const removeRecommendation = (section: keyof typeof activeRecommendations) => {
    setActiveRecommendations({
      ...activeRecommendations,
      [section]: false
    });
    
    // Reset the related state if needed
    if (section === 'refine') {
      setSelectedRefineOption(null);
    } else if (section === 'enhance') {
      setSelectedEnhanceOption(null);
    }
  };
  
  // Add these handlers to view proposals and solutions
  const handleViewProposal = (id: string) => {
    // This function should open the SavvyDataDemo Proposal
    console.log(`Opening proposal with ID: ${id}`);
    
    // Here should be the logic to open/display the proposal
    // For example:
    setSelectedProposalId(id);
    setSelectedSolutionId(null); // Reset solution when viewing a proposal
    setSelectedFunction('viewProposal');
    
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
  };
  
  const handleViewSolution = (id: string) => {
    // Combine solutions from both sources
    const allSolutions = [...solutions, ...extraSolutions];
    
    // Check if the solution exists
    const solutionExists = allSolutions.some(s => s.id === id);
    
    if (!solutionExists) {
      console.warn(`Warning: Attempted to view non-existent solution with ID: ${id}`);
      return;
    }
    
    setSelectedSolutionId(id);
    setSelectedFunction('viewSolution');
    
    // Dismiss welcome guide if it's showing
    if (showWelcomeGuide) {
      setShowWelcomeGuide(false);
    }
  };
  
  // Add state variables to track document tools
  // eslint-disable-next-line no-unused-vars
  const [showDocumentTools, setShowDocumentTools] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [documentToolType, setDocumentToolType] = useState<'document' | 'spreadsheet' | null>(null);
  // eslint-disable-next-line no-unused-vars
  const [documentContent, setDocumentContent] = useState<string>('');
  // eslint-disable-next-line no-unused-vars
  const [spreadsheetData, setSpreadsheetData] = useState<any[][]>([]);
  // eslint-disable-next-line no-unused-vars
  const [spreadsheetColumns, setSpreadsheetColumns] = useState<any[]>([]);

  // Add useEffect to check for uploaded files or links when component mounts
  useEffect(() => {
    // Check localStorage for uploaded files
    const uploadedFile = localStorage.getItem('uploadedFile');
    if (uploadedFile) {
      // Handle file display based on type
      const fileInfo = JSON.parse(uploadedFile);
      if (fileInfo.type.includes('spreadsheet') || fileInfo.type.includes('csv')) {
        setDocumentToolType('spreadsheet');
        // In a real app, you would load the file content
      } else if (fileInfo.type.includes('document') || fileInfo.type.includes('text')) {
        setDocumentToolType('document');
        // In a real app, you would load the file content
      }
    }
    
    // Check localStorage for linked URLs
    const linkedUrl = localStorage.getItem('linkedUrl');
    if (linkedUrl) {
      // In a real app, you would handle displaying linked content
    }
  }, []);

  // Add navigation history state
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);

  // Update the setToolWithHistory function
  const setToolWithHistory = (tool: 'solutions' | 'analyze' | 'plan' | 'executives' | 'docs' | null) => {
    // Always hide welcome guide when a tool is selected
    setShowWelcomeGuide(false);
    
    // Rest of your existing function...
    
    // First, clear any potentially conflicting UI elements
    setSelectedFunction('');
    
    // Then set the selected tool
    if (selectedTool) {
      setNavigationHistory(prev => [...prev, selectedTool]);
    }
    setSelectedTool(tool);
    
    // Add a notification for better UX
    if (tool) {
      addNotification(`Switched to ${tool} tool`, 'info');
    }
  };

  // Function to go back to previous tool
  const goBackToPreviousTool = () => {
    if (navigationHistory.length > 0) {
      // Get the last tool from history
      const prevHistory = [...navigationHistory];
      const prevTool = prevHistory.pop() as 'solutions' | 'analyze' | 'plan' | 'executives' | 'docs' | null;
      
      // Update history and selected tool
      setNavigationHistory(prevHistory);
      setSelectedTool(prevTool);
    } else {
      // If no history, just go back to null
      setSelectedTool(null);
    }
  };

  // Update the handleBentoSelect function to properly handle executives
  const handleBentoSelect = (optionId: string) => {
    // Close menu
    handleBentoClose();
    
    if (optionId === 'library') {
      // Show solution library
      showSolutionLibrary();
      return;
    }
    
    // Rest of your existing function...
    
    // Always hide welcome guide when a bento option is selected
    setShowWelcomeGuide(false);
    
    // Close any open menus
    handleBentoClose();
    
    console.log('Selected bento option:', optionId); // Add logging for debugging
    
    // Reset any other open state that might interfere
    setShowDocumentTools(false);
    
    // Clear the selected function if a tool is being selected
    if (['solutions', 'analyze', 'plan', 'executives', 'docs'].includes(optionId)) {
      setSelectedFunction('');
    }
    
    // Handle special case for executives
    if (optionId === 'executives') {
      console.log('Setting tool to executives');
      setSelectedTool('executives');
      return;
    }
    
    switch (optionId) {
      case 'solutions':
      case 'analyze':
      case 'plan':
      case 'docs':
        setToolWithHistory(optionId as any); // Cast to correct type
        break;
      case 'chrome':
        // Handle chrome
        addNotification("Chrome integration is coming soon!", "info");
        break;
      case 'api':
        // Handle API
        addNotification("API access is coming soon!", "info");
        break;
      case 'write':
        setShowDocumentTools(true);
        setDocumentToolType('document');
        break;
      case 'sheet':
        setShowDocumentTools(true);
        setDocumentToolType('spreadsheet');
        break;
      default:
        // Add logging for unhandled options
        console.log('Unhandled bento option:', optionId);
        break;
    }
  };

  // Add this function to handle the document tools
  // eslint-disable-next-line no-unused-vars
  const handleDocumentToolSelect = (toolType: 'document' | 'spreadsheet') => {
    // Always hide welcome guide when document tools are selected
    setShowWelcomeGuide(false);
    
    setDocumentToolType(toolType);
    setShowDocumentTools(true);
    setSelectedFunction('');
    setSelectedTool(null);
  };

  // Add this to close the document tools
  // eslint-disable-next-line no-unused-vars
  const handleCloseDocumentTools = () => {
    setShowDocumentTools(false);
    setDocumentToolType(null);
  };

  // Add this to save document content
  const handleSaveDocumentContent = (content: string) => {
    setDocumentContent(content);
    // Additional logic to save the content
  };

  // Add this to save spreadsheet data
  const handleSaveSpreadsheetData = (rows: any, columns: any) => {
    setSpreadsheetData([...rows]);
    setSpreadsheetColumns([...columns]);
    // Additional logic to save the spreadsheet
  };

  // Add this state variable to track the expanded state of the right pane
  const [rightPaneExpanded, setRightPaneExpanded] = useState<boolean>(false);

  // Add this hook to detect mobile screen size
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Add this effect to automatically expand on mobile in landscape orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      if (isMobile && window.orientation !== undefined) {
        // In landscape mode (orientation is 90 or -90 degrees)
        if (Math.abs(window.orientation as number) === 90) {
          setRightPaneExpanded(true);
        } else {
          setRightPaneExpanded(false);
        }
      }
    };

    // Set initial state based on orientation
    handleOrientationChange();

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobile]);

  // Add this handler to toggle the expanded state
  const toggleRightPaneExpanded = () => {
    setRightPaneExpanded(prev => !prev);
    // When expanding, scroll to top
    if (!rightPaneExpanded && rightPaneRef.current) {
      rightPaneRef.current.scrollTop = 0;
    }
  };

  // Add this state inside the ProblemWorkspace component function body
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  // Add this function inside the component
  const addNotification = (message: string, type: string = 'info') => {
    const id = uuidv4(); // Or use Date.now().toString() if uuid is not available
    const newNotification: AppNotification = { id, message, type };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);
  };

  // Add a removeNotification function to handle manual closing
  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  // Define a specific handler function for updating problem text
  const handleProblemTextChange = (newText: string) => {
    setProblemText(newText);
    // Add any additional logic you need here
  };

  // Add or modify these state variables
  //const [activeView, setActiveView] = useState<'document' | 'proposal'>('document');
  const [currentProposalId, setCurrentProposalId] = useState<string | null>(null);
  const [currentProposalData, setCurrentProposalData] = useState<any>(null);

  // Add these state variables to your component
  const [newProposal, setNewProposal] = useState<{
    id: string;
    title: string;
    description: string;
    status: 'Draft' | 'Pending' | 'Approved' | 'Rejected';
    cost: number;
    createdAt: string;
    timelineType?: 'Short' | 'Medium' | 'Long';
    estimatedTimeline?: number;
    priority?: 'Low' | 'Medium' | 'High';
    riskLevel?: 'Low' | 'Medium' | 'High';
  }>({
    id: `proposal-${Date.now()}`,
    title: problemStatement.split('.')[0] || 'New Proposal',
    description: problemStatement || 'Define your problem here...',
    status: 'Draft',
    cost: 0,
    createdAt: new Date().toISOString(),
    timelineType: 'Medium',
    estimatedTimeline: 4,
    priority: 'Medium',
    riskLevel: 'Low'
  });

  // Combined handler for saving proposal changes
  const handleSaveProposal = (updatedProposal: any) => {
    // Ensure the timestamp is updated
    const proposalWithTimestamp = {
      ...updatedProposal,
      updatedAt: new Date().toISOString()
    };

    // If this is the current active proposal, update its state
    if (proposalWithTimestamp.id === currentProposalId) {
      setCurrentProposalData(proposalWithTimestamp);
    }
    
    // Get existing proposals from localStorage
    const existingProposals = JSON.parse(localStorage.getItem('proposals') || '[]');
    
    // Find if this proposal already exists
    const existingIndex = existingProposals.findIndex((p: any) => p.id === proposalWithTimestamp.id);
    
    // Update or add the proposal
    if (existingIndex !== -1) {
      existingProposals[existingIndex] = proposalWithTimestamp;
    } else {
      existingProposals.push(proposalWithTimestamp);
    }
    
    // Save updated proposals back to localStorage
    localStorage.setItem('proposals', JSON.stringify(existingProposals));
    
    // Show success notification
    addNotification('Proposal saved successfully', 'success');
    
    // Return the updated proposal
    return proposalWithTimestamp;
  };


  // Add a function to handle function selection that preserves document tools state
  const handleFunctionSelection = (functionId: string) => {
    // Always hide welcome guide when a function is selected
    setShowWelcomeGuide(false);
    
    // Special case for executives
    if (functionId === 'executives') {
      setSelectedFunction('surrogates'); // This ensures the right pane shows the surrogates content
      setSelectedTool('executives');
      return;
    }
    
    // Rest of your existing function...
    setSelectedFunction(functionId);
    
    // If it's a specific function that should trigger a tool, handle that
    if (functionId === 'plan') {
      setSelectedTool('plan');
    } else {
      // Clear selected tool if not specifically set
      setSelectedTool(null);
    }
    
    // If opening a specific recommendation section, handle that
    if (functionId === 'proposals' || functionId === 'similar') {
      setActiveRecommendations({
        ...activeRecommendations,
        proposals: true
      });
      
      // Open recommendations drawer if it's closed
      if (!recommendationsOpen) {
        setRecommendationsOpen(true);
      }
    }
  };

  // First, add a check to ensure the role exists in roleInfoMap
  // Add this helper function somewhere before the render
  const getSurrogateRoleInfo = (role?: string) => {
    if (!role) return null;
    
    const lowerCaseRole = role.toLowerCase();
    // Check if the role exists in roleInfoMap
    return roleInfoMap[lowerCaseRole] || null;
  };

  // Add these useEffect hooks for debugging
  useEffect(() => {
    console.log("selectedTool changed to:", selectedTool);
  }, [selectedTool]);

  useEffect(() => {
    console.log("selectedFunction changed to:", selectedFunction);
  }, [selectedFunction]);

  useEffect(() => {
    console.log("navigationHistory updated:", navigationHistory);
  }, [navigationHistory]);

  useEffect(() => {
    console.log("showDocumentTools:", showDocumentTools);
    console.log("documentToolType:", documentToolType);
  }, [showDocumentTools, documentToolType]);

  // Add this near the beginning of renderContent and renderToolContent
  const renderContent = () => {
    // First check for active sub-stepper
    if (activeSubStepper) {
      return (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ color: '#00CC99' }}>
              {activeSubStepper.charAt(0).toUpperCase() + activeSubStepper.slice(1)} Stepper
            </Typography>
            <Button 
              variant="outlined" 
              onClick={closeSubStepper}
              sx={{ 
                color: '#8A2BE2',
                borderColor: 'rgba(138, 43, 226, 0.5)',
                '&:hover': {
                  borderColor: '#8A2BE2',
                  backgroundColor: 'rgba(138, 43, 226, 0.1)',
                }
              }}
            >
              Back
            </Button>
          </Box>
          {renderSubStepper(activeSubStepper, problemText)}
        </Box>
      );
    }

    // Then check for welcome guide
    if (showWelcomeGuide) {
      return (
        <Box sx={{ p: 3 }}>
          <WelcomeGuide 
            onGetStarted={handleDismissWelcomeGuide}
            onOpenNewProposal={openNewProposal}
            onOpenExecutives={() => {
              setSelectedTool('executives');
              setSelectedFunction('');
            }}
            onSelectFunction={handleFunctionSelection}
            onOpenRefineMenu={handleRefineClick}
            onOpenEnhanceMenu={handleEnhanceClick}
            onOpenSolutions={() => {
              setSelectedFunction('solutions');
            }}
            onOpenPlan={() => {
              setSelectedTool('plan');
            }}
            onOpenToolbox={() => {
              setSelectedFunction('toolbox');
            }}
            onOpenSurrogates={() => {
              setSelectedFunction('surrogates');
            }}
            onOpenDemo={() => {
              setSelectedFunction('savvyDemo');
            }}
            onOpenProposals={() => {
              setSelectedFunction('proposals');
            }}
            onOpenFeedback={() => {
              addNotification('Feedback feature coming soon!', 'info');
            }}
            onOpenAttribution={() => {
              window.location.href = '/attribution';
            }}
            onShowComingSoon={(message) => {
              addNotification(message || 'Coming soon!', 'info');
            }}
            onOpenSubStepper={(type) => openSubStepper(type)}
          />
        </Box>
      );
    }
    
    // Then check for selected tool
    if (selectedTool) {
      return renderToolContent();
    }
    
    // Continue with your existing document tools check
    if (showDocumentTools) {
      if (documentToolType === 'document') {
        return (
          <DocumentEditor 
            initialContent={documentContent}
            onSave={handleSaveDocumentContent}
          />
        );
      } else if (documentToolType === 'spreadsheet') {
        return (
          <SpreadsheetEditor
            initialData={spreadsheetData}
            initialColumns={spreadsheetColumns}
            onSave={handleSaveSpreadsheetData}
          />
        );
      }
    }
    
    // Switch statement for selectedFunction
    switch (selectedFunction) {
      case 'surrogates':
      return (
          <Box sx={{ p: 3 }}>
            {selectedExecutive && selectedExecutive.role && getSurrogateRoleInfo(selectedExecutive.role) && (
              <SurrogateRoleInfo
                roleKey={selectedExecutive.role.toLowerCase()}
                roleInfo={roleInfoMap[selectedExecutive.role.toLowerCase()]}
                onInfoChange={handleRoleInfoChange}
              />
            )}
            {/* Your existing surrogates content here */}
          </Box>
        );
      
      case 'savvyDemo':
      return (
          <Box sx={{ p: 3 }}>
            <SavvyDataDemoContent 
          onBack={() => {
                setSelectedFunction('surrogates');
              }}
            />
          </Box>
        );
      
      case 'write':
      return (
          <Box sx={{ p: 3 }}>
            {showDocumentTools && (
              <DocumentTools
                initialTab={documentToolType === 'document' ? 0 : 1}
                problemText={problemText}
                setProblemText={handleProblemTextChange}
                onSaveDocument={handleSaveDocumentContent}
                onSaveSpreadsheet={handleSaveSpreadsheetData}
                onAddDocumentToProposal={handleAddToProposal}
              />
            )}
          </Box>
        );
      
      case 'proposal':
        return (
          <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {currentProposalId && (
              currentProposalData ? (
                <BlankProposalTemplate 
                  proposalId={currentProposalId}
                  initialTitle={currentProposalData.title}
                  initialDescription={currentProposalData.description}
                  initialContent={currentProposalData.content}
                  amount={currentProposalData.amount || 0}
                  status={currentProposalData.status || 'draft'}
                  onSave={handleSaveProposal}
                  onBack={() => navigateToDocumentTools()}
                />
              ) : (
                <ProposalDetailContent 
                  proposalId={currentProposalId}
                  onBack={() => navigateToDocumentTools()}
                  onViewSolution={(solutionId) => {
                    console.log(`Viewing solution: ${solutionId}`);
                    handleViewSolution(solutionId);
                  }}
                />
              )
            )}
          </Box>
        );
      
      case 'businessPlan':
        return (
          <Box sx={{ 
            p: 3, 
            height: '100%', 
            overflow: 'auto',
            '& .MuiStep-root': {
              width: 'auto', // Override any problematic width settings
            },
            '& .MuiStepLabel-root': {
              flexDirection: 'column',
            }
          }}>
            <BusinessPlanStepper
              companyName="Savvy Data"
              productName="AI Analytics Platform"
                initialStep={0}
                onComplete={handleStepperComplete}
            />
          </Box>
        );
      
      case 'viewProposal':
        return (
          <Box sx={{ p: 3 }}>
            {selectedProposalId && (
              <SavvyDataProposalContent
                id={selectedProposalId}
                onBack={() => {
                  setSelectedProposalId(null);
                  setSelectedFunction('surrogates');
                }}
              />
            )}
          </Box>
        );
      
      case 'viewSolution':
        return (
          <Box sx={{ p: 3 }}>
            {selectedSolutionId && (
              <SolutionDetailContent
                solutionId={selectedSolutionId}
                onBack={() => {
                  setSelectedSolutionId(null);
                  setSelectedFunction('solutions');
                }}
              />
            )}
          </Box>
        );
      
      case 'toolbox':
                return (
          <Box sx={{ p: 3 }}>
            <ProblemSolvingToolboxContent
              problemStatement={problemText}
              onCostChange={(newCost) => {
                console.log(`Updated cost: ${newCost}`);
              }}
              currentCost={0}
            />
          </Box>
        );
      
      default:
        // Show the welcome guide or default content
        return (
          <Box sx={{ p: 3 }}>
            {showWelcomeGuide ? (
              <WelcomeGuide 
                onGetStarted={handleDismissWelcomeGuide}
                onOpenExecutives={() => {
                  setSelectedTool('executives');
                  setSelectedFunction('');
                }}
                onSelectFunction={handleFunctionSelection}
                onOpenRefineMenu={handleRefineClick}
                onOpenEnhanceMenu={handleEnhanceClick}
                onOpenSolutions={() => {
                  setSelectedFunction('solutions');
                }}
                onOpenPlan={() => {
                  setSelectedTool('plan');
                }}
                onOpenToolbox={() => {
                  setSelectedFunction('toolbox');
                }}
                onOpenSurrogates={() => {
                  setSelectedFunction('surrogates');
                }}
                onOpenDemo={() => {
                  setSelectedFunction('savvyDemo');
                }}
                onOpenProposals={() => {
                  setSelectedFunction('proposals');
                }}
                onOpenFeedback={() => {
                  addNotification('Feedback feature coming soon!', 'info');
                }}
                onOpenAttribution={() => {
                  window.location.href = '/attribution';
                }}
                onShowComingSoon={(message) => {
                  addNotification(message || 'Coming soon!', 'info');
                }}
                onOpenSubStepper={(type) => openSubStepper(type)}
              />
            ) : (
          <Box>
                {/* Default content */}
                <Typography variant="h6">
                  Please select a function from the menu
                      </Typography>
                    </Box>
            )}
                  </Box>
        );
    }
  };
  
  // Get the selected function label
  const getSelectedFunctionLabel = () => {
    const selectedOption = mainFunctionOptions.find(option => option.id === selectedFunction);
    return selectedOption ? selectedOption.label : 'Menu';
  };

  // Modify the HolographicStepper onComplete handler to show a proposal
  // This would be in any component that contains a HolographicStepper
  const handleStepperComplete = () => {
    addNotification("Business plan successfully completed!", "success");
    // You can still navigate to a proposal if needed
    const proposalId = proposals[0].id;
    handleViewProposal(proposalId);
  };
  
  //const handleToolboxClick = () => {
  //  setSelectedFunction('toolbox');
  //};
  
  console.log('problemText:', problemText);
  console.log('setProblemText type:', typeof setProblemText);
  
  // Add these state variables
  const [proposalsAnchorEl, setProposalsAnchorEl] = useState<null | HTMLElement>(null);
  const [solutionsAnchorEl, setSolutionsAnchorEl] = useState<null | HTMLElement>(null);

  // Create a list of proposal options including a "Create New" option
  const proposalOptions = [
    'Create New Proposal',
    'Marketing Campaign Proposal',
    'Financial Analysis Proposal'
  ];

  // Add handler functions for the Proposals dropdown
  const handleProposalsClick = (event: React.MouseEvent<HTMLElement>) => {
    setProposalsAnchorEl(event.currentTarget);
  };

  const handleProposalsClose = () => {
    setProposalsAnchorEl(null);
  };

  // Update the handleProposalSelect function to ensure it properly updates all state
  const handleProposalSelect = (option: string) => {
    handleProposalsClose();
    
    if (option === 'Create New Proposal') {
      // Generate a new proposal ID
      const newProposalId = `proposal-${Date.now()}`;
      
      // Create and set a new blank proposal with consistent properties
      const newProposal = {
        id: newProposalId,
        title: 'New Proposal',
        description: 'Describe your proposal here...',
        content: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        estimatedTimeline: '3 months',
        timelineType: 'initiative',
        status: 'draft',
        cost: 0,
        amount: 0,
        priority: 'Medium',
        riskLevel: 'Low'
      };
      
      // Update state
      setCurrentProposalId(newProposalId);
      setCurrentProposalData(newProposal);
      
      // Switch to proposal view
      setShowDocumentTools(false);
      setSelectedFunction('proposal');
      
      // Save to localStorage 
      const existingProposals = JSON.parse(localStorage.getItem('proposals') || '[]');
      localStorage.setItem('proposals', JSON.stringify([...existingProposals, newProposal]));
    }
  };

  // Create a list of solution options
  const solutionOptions = [
    'Solution Library',
    'Business Plan Demo',
    'SavvyData Demo'
  ];

  // Add handler functions for the Solutions dropdown
  const handleSolutionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSolutionsAnchorEl(event.currentTarget);
  };

  const handleSolutionsClose = () => {
    setSolutionsAnchorEl(null);
  };

  // Update the handleSolutionSelect function to handle the different options properly
  const handleSolutionSelect = (option: string) => {
    handleSolutionsClose();
    
    // Handle the selection based on the option
    switch (option) {
      case 'Business Plan':
        setSelectedFunction('businessPlan');
        setShowDocumentTools(false);
        break;
        
      case 'SavvyData Demo':
        console.log('Opening SavvyData Demo');
        setSelectedFunction('savvyDemo');
        setShowDocumentTools(false);
        break;
        
      case 'Solution Library':
        console.log('Opening Solution Library');
        setSelectedFunction('solutions');
        setShowDocumentTools(false);
        break;
        
      default:
        break;
    }
  };

  // This function is declared but never used, let's make it more functional:
  const navigateToDocumentTools = () => {
    setSelectedFunction('write');
    setShowDocumentTools(true);
    setDocumentToolType('document'); // Default to document editor
    
    // Optionally add a notification
    addNotification("Switched to document editor", "info");
  };

  // Add to the renderToolContent function
  const renderToolContent = () => {
    console.log("renderToolContent called with selectedTool:", selectedTool);
    
    console.log('Rendering tool content for:', selectedTool); // Add debugging
    
    switch (selectedTool) {
      case 'solutions':
                return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Solutions Explorer</Typography>
            {/* Solutions components */}
            <Paper sx={{ p: 3, bgcolor: 'rgba(26, 26, 46, 0.7)' }}>
              <Typography variant="body1">
                Explore potential solutions to your problem.
              </Typography>
              {/* Add your solutions components here */}
            </Paper>
          </Box>
        );
        
      case 'analyze':
          return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Problem Analysis</Typography>
            {/* Analysis components */}
            <Paper sx={{ p: 3, bgcolor: 'rgba(26, 26, 46, 0.7)' }}>
              <Typography variant="body1">
                Comprehensive analysis of your problem statement.
            </Typography>
              {/* Add your analysis components here */}
            </Paper>
          </Box>
        );
      
      case 'plan':
        return (
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            '& .MuiStep-root': {
              width: 'auto',
            },
            '& .MuiStepLabel-root': {
              flexDirection: 'column',
            }
          }}>
            <PlanStepper
              problemStatement={problemText || problemStatement}
              initialStep={0}
              onComplete={() => {
                goBackToPreviousTool();
                addNotification("Plan has been generated successfully.", "success");
              }}
              onBack={goBackToPreviousTool}
            />
          </Box>
        );
      
      case 'executives':
        return (
          <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ color: '#00CC99' }}>
                Executive Perspectives
              </Typography>
              <Box>
                <Tooltip title="Problem Solving Toolbox">
                  <IconButton 
                    onClick={() => setShowProblemSolvingToolbox(!showProblemSolvingToolbox)}
                    sx={{ 
                      color: showProblemSolvingToolbox ? '#00CC99' : 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00CC99' }
                    }}
                  >
                    <ToolsIcon />
                  </IconButton>
                </Tooltip>
              <Button 
                variant="outlined"
                  onClick={goBackToPreviousTool}
                sx={{ 
                    ml: 1,
                    color: '#8A2BE2',
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                  '&:hover': {
                      borderColor: '#8A2BE2',
                      backgroundColor: 'rgba(138, 43, 226, 0.1)',
                  }
                }}
              >
                  Back
              </Button>
              </Box>
            </Box>
            
            {showProblemSolvingToolbox && (
              <Box sx={{ mb: 3 }}>
                <ProblemSolvingToolboxContent
                  problemStatement={problemText}
                  onCostChange={(newCost) => {
                    console.log(`Updated cost: ${newCost}`);
                    // Add your cost handling logic here
                  }}
                  currentCost={0}
                />
              </Box>
            )}
            
            <ExecutiveStepper 
              executives={executivesData}
              problemStatement={problemText}
              onBack={goBackToPreviousTool}
              onComplete={() => {
                goBackToPreviousTool();
                addNotification("Executive insights have been generated successfully.", "success");
              }}
              onViewDetailedPlan={(planType) => {
                openSubStepper(planType);
              }}
            />
          </Box>
        );
        
      case 'docs':
      return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Documents</Typography>
            <DocumentTools
              initialTab={documentToolType === 'document' ? 0 : 1}
              problemText={problemText}
              setProblemText={handleProblemTextChange}
              onSaveDocument={handleSaveDocumentContent}
              onSaveSpreadsheet={handleSaveSpreadsheetData}
              onAddDocumentToProposal={handleAddToProposal}
              onClose={handleCloseDocumentTools}
          />
        </Box>
      );
      
      default:
        return null;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderSubStepper = (type: string, problemText: string) => {
    switch (type) {
      case 'strategic':
      return (
          <StrategicPlanStepper
            companyName="Your Company"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Strategic plan added to proposal successfully.", "success");
            }}
          />
        );
      case 'technology':
    return (
          <TechRoadmapStepper
            productName="Your Product"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Technology roadmap added to proposal successfully.", "success");
            }}
          />
        );
      case 'marketing':
        return (
          <MarketingPlanStepper
            companyName="Your Market"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Marketing plan added to proposal successfully.", "success");
            }}
          />
        );
      case 'business':
        return (
          <BusinessPlanStepper
            companyName="Savvy Data"
            productName="AI Analytics Platform"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Business plan added to proposal successfully.", "success");
            }}
          />
        );
      case 'product':
        return (
          <ProductStrategyStepper
            productName="Your Product"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Product roadmap added to proposal successfully.", "success");
            }}
          />
        );
      case 'financial':
        return (
          <FinancialPlanStepper
            productName="Your Finances"
            initialStep={0}
            onComplete={closeSubStepper}
            onSubmitRFP={() => {
              closeSubStepper();
              addNotification("Financial plan added to proposal successfully.", "success");
            }}
          />
        );
      case 'executives':
        return (
          <ExecutiveStepper
            executives={executivesData}
            problemStatement={problemText}
            problemText={problemText}
            setProblemText={handleProblemTextChange}
            problemTitle={problemStatement.substring(0, 30) + '...'}
            initialStep={0}
            onComplete={closeSubStepper}
            onBack={closeSubStepper}
            onViewDetailedPlan={(planType) => {
              closeSubStepper();
              openSubStepper(planType);
            }}
            handleViewProposal={handleViewProposal}
            proposals={proposals}
            handleStepperComplete={handleStepperComplete}
          />
        );
      // Add other cases for marketing and financial plans
      default:
        return null;
    }
  };

  const openSubStepper = (type: string) => {
    setShowWelcomeGuide(false);
    setActiveSubStepper(type);
    setSelectedFunction('');
    setSelectedTool(null);
    // Clear other selections to focus on the sub-stepper
    setSelectedProposalId(null);
    setSelectedSolutionId(null);
    setShowDocumentTools(false);
    // Add any other state clearing needed
    // Add notification
    addNotification(`Opening ${type} view`, 'info');
  };

  // Add a function to close the sub-stepper
  const closeSubStepper = () => {
    setActiveSubStepper(null);
  };

  // Add this to your state variables (remove the eslint-disable comment)
  const [activeSubStepper, setActiveSubStepper] = useState<string | null>(null);

  // Add this in your component to log state changes
  useEffect(() => {
    console.log('selectedTool changed:', selectedTool);
  }, [selectedTool]);

  useEffect(() => {
    console.log('selectedFunction changed:', selectedFunction);
  }, [selectedFunction]);

  // Add this useEffect to update the proposal when the problem statement changes
  useEffect(() => {
    if (problemStatement) {
      setNewProposal(prev => ({
        ...prev,
        title: problemStatement.split('.')[0] || prev.title,
        description: problemStatement
      }));
    }
  }, [problemStatement]);

  // Add this function to handle adding a document to a proposal
  const handleAddToProposal = (content: string, cost: number = 0) => {
    // Create a new proposal or update an existing one with the document content
    const proposalToUpdate = {
      ...newProposal,
      content: content,
      cost: newProposal.cost + cost,
      updatedAt: new Date().toISOString()
    };
    
    // Call the save proposal function
    handleSaveProposal(proposalToUpdate);
    
    // Show notification
    addNotification('Document added to proposal successfully', 'success');
    
    // Optionally redirect to view the proposal
    setSelectedFunction('proposal');
    setCurrentProposalId(newProposal.id);
    setCurrentProposalData(proposalToUpdate);
  };
  
  // Add this function to handle AI submission of proposals
  const handleSubmitProposalToAI = async (proposalData: any) => {
    // Show notification that we're processing
    addNotification('Processing proposal with AI...', 'info');
    
    try {
      // In a real implementation, you would make an API call to your AI service
      // For now we'll simulate a delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update the proposal with AI-enhanced content
      const aiEnhancedProposal = {
        ...proposalData,
        status: 'Pending',
        aiEnhanced: true,
        lastAiUpdate: new Date().toISOString()
      };
      
      // Save the enhanced proposal
      handleSaveProposal(aiEnhancedProposal);
      
      // Set it as the current proposal
      setCurrentProposalId(proposalData.id);
      setCurrentProposalData(aiEnhancedProposal);
      
      // Show success notification
      addNotification('AI analysis complete! Proposal has been enhanced.', 'success');
      
      // Optionally, switch to proposal view
      setSelectedFunction('proposal');
    } catch (error) {
      console.error('Error processing proposal with AI:', error);
      addNotification('Failed to process proposal with AI. Please try again.', 'error');
    }
  };
  
  // Add this function
  const showSolutionLibrary = () => {
    // Reset other UI states
    setSelectedFunction('solutions');
    setSelectedTool(null);
    setShowDocumentTools(false);
    setSelectedExecutive(null);
    
    // Show the welcome guide
    setShowWelcomeGuide(true);
  };
  
  // Add this function to open the BlankProposalTemplate
  const openNewProposal = () => {
    // Hide welcome guide
    setShowWelcomeGuide(false);
    
    // Clear other selections
    setSelectedTool(null);
    setShowDocumentTools(false);
    
    // Create a new proposal ID
    const newProposalId = `proposal-${Date.now()}`;
    
    // Set up the proposal
    setCurrentProposalId(newProposalId);
    setCurrentProposalData({
      id: newProposalId,
      title: problemStatement ? problemStatement.split('.')[0] : 'New Proposal',
      description: problemStatement || 'Define your proposal here...',
      content: '',
      status: 'Draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // Set function to 'proposal' to show the BlankProposalTemplate
    setSelectedFunction('proposal');
    
    // Add notification
    addNotification('New proposal created', 'success');
  };
  
  // Add this with your other state variables
  const [showProblemSolvingToolbox, setShowProblemSolvingToolbox] = useState<boolean>(false);
  
  return (
    <WorkspaceContainer>
      <BackgroundOverlay />
      {showBackgroundAnimation && <BackgroundAnimation />}
      
      {/* Removed top app bar section */}
      
      {/* Split Screen Content - adjust height to take full screen */}
      <SplitScreenContainer sx={{
        transition: 'all 0.3s ease',
        height: '100vh', // Changed from calc(100vh - 64px) since app bar is removed
      }}>
        {/* Left Pane with integrated toolbar */}
        <LeftPane expanded={rightPaneExpanded}>
          {/* Left pane content with toolbar moved inside */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Add the navigation toolbar here */}
            <Box sx={{ 
              mb: 3, 
              background: 'rgba(26, 26, 46, 0.7)',
              backdropFilter: 'blur(5px)',
              borderRadius: theme.spacing(1),
              border: '1px solid rgba(138, 43, 226, 0.2)',
            }}>
              <StyledToolbar sx={{ 
                minHeight: 'unset', 
                py: 1,
                px: 2,
                flexWrap: 'wrap',
                gap: 1,
              }}>
          
          {/* Main Function Dropdown */}
          <NavItem onClick={handleMainFunctionClick}>
            {mainFunctionOptions.find(option => option.id === selectedFunction)?.icon}
            <Typography variant="body1" sx={{ ml: 1, mr: 0.5 }}>
              {getSelectedFunctionLabel()}
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" />
          </NavItem>
          <StyledMenu
            anchorEl={mainFunctionAnchorEl}
            open={Boolean(mainFunctionAnchorEl)}
            onClose={handleMainFunctionClose}
          >
            {mainFunctionOptions.map((option) => (
              <StyledMenuItem 
                key={option.id}
                onClick={() => handleMainFunctionSelect(option.id)}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText primary={option.label} />
              </StyledMenuItem>
            ))}
          </StyledMenu>
          
                {/* Proposals Dropdown */}
                <NavItem 
                  onClick={handleProposalsClick}
                  selected={selectedFunction === 'proposal'}
                >
            <Typography variant="body1" sx={{ mr: 0.5 }}>
                    Proposals
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" />
          </NavItem>
          <StyledMenu
                  anchorEl={proposalsAnchorEl}
                  open={Boolean(proposalsAnchorEl)}
                  onClose={handleProposalsClose}
                >
                  {proposalOptions.map((option) => (
                    <StyledMenuItem key={option} onClick={() => handleProposalSelect(option)}>
                {option}
              </StyledMenuItem>
            ))}
          </StyledMenu>
          
                {/* Solutions Dropdown */}
                <NavItem 
                  onClick={handleSolutionsClick}
                  selected={['businessPlan', 'savvyDemo', 'solutions'].includes(selectedFunction)}
                >
            <Typography variant="body1" sx={{ mr: 0.5 }}>
                    Solutions
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" />
                </NavItem>
                <StyledMenu
                  anchorEl={solutionsAnchorEl}
                  open={Boolean(solutionsAnchorEl)}
                  onClose={handleSolutionsClose}
                >
                  {solutionOptions.map((option) => (
                    <StyledMenuItem key={option} onClick={() => handleSolutionSelect(option)}>
                      {option}
                    </StyledMenuItem>
                  ))}
                </StyledMenu>
            {/* Bento Menu */}
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleBentoClick}
            >
              <AppsIcon />
            </IconButton>
            <StyledMenu
              anchorEl={bentoAnchorEl}
              open={Boolean(bentoAnchorEl)}
              onClose={handleBentoClose}
              PaperProps={{
                sx: { width: 320, maxWidth: '100%' }
              }}
            >
              <BentoGrid container spacing={2}>
                {bentoOptions.map((option) => (
                  <Grid item xs={4} key={option.id}>
                    <BentoItem onClick={() => handleBentoSelect(option.id)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {option.icon}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {option.label}
                        </Typography>
                      </Box>
                    </BentoItem>
                  </Grid>
                ))}
              </BentoGrid>
            </StyledMenu>
            {/* Executives Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    edge="start"
                    onClick={toggleExecutivesDrawer}
                    sx={{ 
                      color: 'rgba(138, 43, 226, 0.8)',
                      '&:hover': {
                        color: 'rgba(0, 255, 255, 0.8)',
                        backgroundColor: 'rgba(0, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
              <InfoTooltip 
                title={
                  <React.Fragment>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Executive Surrogates</Typography>
                    <Typography variant="body2">
                          Access AI-powered executive advisors to help solve your problem from different perspectives
                    </Typography>
                  </React.Fragment>
                }
                    placement="right"
              />
            </Box>
                {/* Background Animation Toggle - Moved to the right */}
                <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showBackgroundAnimation}
                onChange={(e) => setShowBackgroundAnimation(e.target.checked)}
                color="primary"
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#8A2BE2',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'rgba(138, 43, 226, 0.5)',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'white' }}>
                Toggle Background
              </Typography>
            }
            labelPlacement="start"
          />
                </Box>
        </StyledToolbar>
            </Box>
      
            {/* Fix executives drawer implementation */}
      <StyledDrawer
        anchor="left"
        open={executivesDrawerOpen}
        onClose={toggleExecutivesDrawer}
      >
        <Box sx={{ p: 3 }}>
          <GradientText gradient="primary" variant="h5" sx={{ mb: 3 }}>
            Executive Surrogates
          </GradientText>
          
          {executivesData.map((executive) => (
            <ExecutiveCard 
              key={executive.id}
                    onClick={() => handleExecutiveSelect(executive)}
              sx={{ 
                borderColor: selectedExecutive?.id === executive.id 
                  ? 'primary.main' 
                  : 'rgba(138, 43, 226, 0.2)'
              }}
            >
              <CardHeader
                title={
                  <Typography variant="h6">
                    {executive.role} - {executive.name}
                  </Typography>
                }
                subheader={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {executive.title}
                  </Typography>
                }
              />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Specialty: {executive.specialty}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Rating: {executive.rating}/5.0
                </Typography>
                <Typography variant="body2">
                  Completed Tasks: {executive.completedTasks}
                </Typography>
              </CardContent>
            </ExecutiveCard>
          ))}
        </Box>
      </StyledDrawer>

            {/* Original Left Pane content continues here */}
            <Box sx={{ position: 'relative', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <GradientText gradient="secondary" variant="h5" sx={{ fontWeight: 600 }}>
                    Current Problem Statement
                  </GradientText>
                </Box>
                <Tooltip title="Back to Solution Library">
                  <IconButton
                    onClick={showSolutionLibrary}
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00CC99' }
                    }}
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper 
                        sx={{ 
                p: 2,
                background: 'rgba(26, 26, 46, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: theme.spacing(1),
                border: '1px solid rgba(138, 43, 226, 0.2)',
                }}>
                  <Box 
                        sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      height: '100%',
                      overflow: 'hidden'  // Prevents overflow at container level
                    }}
                  >
                    <NavigationStepper
                      problemText={problemText}
                      setProblemText={handleProblemTextChange}
                      handleExecutiveSelect={handleExecutiveSelect}
                      toggleExecutivesDrawer={toggleExecutivesDrawer}
                      executives={executivesData}
                      handleMainFunctionSelect={handleMainFunctionSelect}
                      handleViewProposal={handleViewProposal}
                      proposals={proposals}
                      problemTitle={problemStatement.substring(0, 30) + '...'}
                      handleStepperComplete={handleStepperComplete}
                    />
                    </Box>
              </Paper>

              <Divider sx={{ my: 3, borderColor: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' }} />
              
              <Paper 
                sx={{ 
                  p: 2, 
                  mt: 2, 
                  background: 'rgba(26, 26, 46, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(138, 43, 226, 0.2)',
                }}
              >
                {/* Add the EditableProposalCard in the appropriate place */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <EditableProposalCard
                    initialProposal={newProposal}
                    onSave={handleSaveProposal}
                    onSubmitToAI={handleSubmitProposalToAI}
                    onClick={() => {
                      // Handle click actions, such as viewing the proposal in detail
                      setSelectedFunction('proposal');
                      setCurrentProposalId(newProposal.id);
                      setCurrentProposalData(newProposal);
                    }}
                  />
              </Box>
              </Paper>
            </Box>
            
            {/* Recommendations Drawer Handle */}
            <Divider sx={{ my: 3, borderColor: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' }} />
            {/* Recommendations Drawer Handle */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <DrawerHandle onClick={toggleRecommendations}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#00CC99',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Recommendations
                  {recommendationsOpen ? 
                    <KeyboardArrowUpIcon sx={{ ml: 1 }} /> : 
                    <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                  }
                </Typography>
              </DrawerHandle>
            </Box>
            
            {/* Recommendations Content */}
            <Collapse in={recommendationsOpen} timeout="auto" unmountOnExit>
              <Box sx={{ 
                background: 'rgba(26, 26, 46, 0.7)',
                backdropFilter: 'blur(15px)',
                borderRadius: theme.spacing(1.5),
                border: '1px solid rgba(138, 43, 226, 0.3)',
                overflow: 'hidden',
              }}>
                {/* Executive Recommendation */}
                {activeRecommendations.executive && selectedExecutive && (
                  <Box sx={{ 
                    p: 3, 
                    borderBottom: activeRecommendations.proposals || activeRecommendations.function || activeRecommendations.refine || activeRecommendations.enhance ? 
                      '1px solid rgba(138, 43, 226, 0.2)' : 'none'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Recommended Executive Surrogate
                      </GradientText>
                      <IconButton 
                        size="small" 
                        onClick={() => removeRecommendation('executive')}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: 'rgba(138, 43, 226, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        <PeopleIcon sx={{ color: '#8A2BE2' }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {selectedExecutive.name} ({selectedExecutive.role})
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {selectedExecutive.title}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={() => dismissWelcomeGuideAndDo(() => setSelectedFunction('surrogates'))}
                        sx={{ 
                          background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                          color: '#fff',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
                            boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
                          },
                        }}
                      >
                        Work with {selectedExecutive.role}
                      </Button>
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={() => dismissWelcomeGuideAndDo(() => setSelectedFunction('toolbox'))}
                        sx={{ 
                          background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                          color: '#fff',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
                            boxShadow: '0 0 15px rgba(138, 43, 226, 0.5)',
                          },
                        }}
                      >
                        Refine {selectedExecutive.role} Solution
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => dismissWelcomeGuideAndDo(() => setSelectedFunction('surrogates'))}
                        sx={{ 
                          borderColor: 'rgba(138, 43, 226, 0.5)',
                          color: '#fff',
                          '&:hover': {
                            borderColor: 'rgba(138, 43, 226, 0.8)',
                            background: 'rgba(138, 43, 226, 0.1)',
                          }
                        }}
                      >
                        Edit Surrogate Commands
                      </Button>
                    </Box>
                  </Box>
                )}
                
                {/* Main Function Recommendation */}
                {activeRecommendations.function && (
                  <Box sx={{ 
                    p: 3, 
                    borderBottom: activeRecommendations.proposals || activeRecommendations.refine || activeRecommendations.enhance ? 
                      '1px solid rgba(138, 43, 226, 0.2)' : 'none'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Selected Function
                      </GradientText>
                      <IconButton 
                        size="small" 
                        onClick={() => removeRecommendation('function')}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: 'rgba(138, 43, 226, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        {mainFunctionOptions.find(option => option.id === selectedFunction)?.icon || <BuildIcon sx={{ color: '#8A2BE2' }} />}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {mainFunctionOptions.find(option => option.id === selectedFunction)?.label || 'Selected Function'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Refine Recommendation */}
                {activeRecommendations.refine && selectedRefineOption && (
                  <Box sx={{ 
                    p: 3, 
                    borderBottom: activeRecommendations.proposals || activeRecommendations.enhance ? 
                      '1px solid rgba(138, 43, 226, 0.2)' : 'none'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Refine Focus
                      </GradientText>
                      <IconButton 
                        size="small" 
                        onClick={() => removeRecommendation('refine')}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: 'rgba(138, 43, 226, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        <EditIcon sx={{ color: '#8A2BE2' }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedRefineOption}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Enhance Recommendation */}
                {activeRecommendations.enhance && selectedEnhanceOption && (
                  <Box sx={{ 
                    p: 3, 
                    borderBottom: activeRecommendations.proposals ? 
                      '1px solid rgba(138, 43, 226, 0.2)' : 'none'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Enhancement Focus
                      </GradientText>
                      <IconButton 
                        size="small" 
                        onClick={() => removeRecommendation('enhance')}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: 'rgba(138, 43, 226, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        <LightbulbIcon sx={{ color: '#8A2BE2' }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedEnhanceOption}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Active Proposals */}
                {activeRecommendations.proposals && (
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Active Proposals, Drafts & Solution Demo
                      </GradientText>
                      <IconButton 
                        size="small" 
                        onClick={() => removeRecommendation('proposals')}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Grid container spacing={2}>
                      {proposals.slice(0, 2).map((proposal: any) => {
                        // Ensure proposal has all required properties
                        const safeProposal = {
                          id: proposal.id,
                          title: proposal.title,
                          description: proposal.description || '',
                          amount: Number(proposal.amount) || 0,
                          status: proposal.status || 'Draft',
                          createdAt: proposal.createdAt || new Date().toISOString(),
                          updatedAt: proposal.updatedAt || new Date().toISOString(),
                          category: proposal.category || 'General',
                          createdBy: proposal.createdBy || 'User',
                          updatedBy: proposal.updatedBy || 'User'
                        };
                        
                        return (
                          <Grid item xs={12} key={proposal.id}>
                            <ProposalCard 
                              proposal={safeProposal}
                              onClick={() => handleViewProposal(safeProposal.id)}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>

                    <Box sx={{ p: 2, mt: 2 }}>
                      <GradientText gradient="primary" variant="h6">
                        Solution Demo
                      </GradientText>
                    </Box>

                    <Grid container spacing={4}>
                      {proposals.slice(0, 1).map((proposal: any) => {
                        // Ensure proposal has all required properties
                        const safeProposal = {
                          id: proposal.savvydata,
                          title: proposal.title,
                          description: proposal.description || '',
                          amount: Number(proposal.amount) || 0,
                          status: proposal.status || 'Draft',
                          createdAt: proposal.createdAt || new Date().toISOString(),
                          updatedAt: proposal.updatedAt || new Date().toISOString(),
                          category: proposal.category || 'General',
                          createdBy: proposal.createdBy || 'User',
                          updatedBy: proposal.updatedBy || 'User'
                        };
                        
                        return (
                          <Grid item xs={12} key={proposal.id}>
                            <SavvyDataProposalCard 
                              id={safeProposal.id}
                              title={safeProposal.title}
                              description={safeProposal.description}
                              amount={safeProposal.amount}
                              status={safeProposal.status}
                              onView={() => handleViewProposal(safeProposal.id)}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    
                    {proposals.length > 2 && (
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button 
                          variant="text" 
                          size="small"
                          onClick={() => navigate('/proposals')}
                          sx={{ color: '#00CC99' }}
                        >
                          View All Proposals
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                
                {/* Empty State */}
                {!activeRecommendations.executive && 
                 !activeRecommendations.function && 
                 !activeRecommendations.refine && 
                 !activeRecommendations.enhance && 
                 !activeRecommendations.proposals && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                      No recommendations added yet.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      Select an executive, function, or refine option to add recommendations.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>

            <Divider sx={{ my: 2, borderColor: 'rgba(138, 43, 226, 0.2)' }} />

            {/* Main Functions Section */}
            <Box sx={{ p: 2, mt: 2 }}>
              <GradientText gradient="primary" variant="h6">
                Problem Solving Options
              </GradientText>
              <MainFunctionCards 
                onSelectFunction={handleMainFunctionSelect}
                selectedFunction={selectedFunction}
              />
            </Box>
          </motion.div>
        </LeftPane>
        
        {/* Right Pane - Results/Output */}
        <RightPane ref={rightPaneRef} expanded={rightPaneExpanded}>
          <Box sx={{ position: 'relative' }}>
            <FullscreenButton onClick={toggleRightPaneExpanded}>
              {rightPaneExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </FullscreenButton>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {renderContent()}
            </motion.div>
          </Box>
        </RightPane>
      </SplitScreenContainer>
      
      <FloatingNotifications 
        notifications={notifications} 
        onClose={removeNotification} 
      />
    </WorkspaceContainer>
  );
};

export default ProblemWorkspace; 